import React, { useState, useEffect } from "react";
import Loader from "components/Loader";
import { Navigate, useLocation } from "react-router-dom";
import api from "services/backendService";
import { useAuthorizationContext } from "contexts/AuthorizationContext";
import { Button } from "reactstrap";

const ExternalLink = () => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [parameters, setParameters] = useState({});

  const { activeOrganization, setActiveOrganization, isOrgFilterEnabled } =
    useAuthorizationContext();

  useEffect(() => {
    loadPagesParametersAsync()
      .catch((err) => {
        setParameters({
          pathname: undefined,
          error: "Something went wrong",
          details: getErrorDetails(err),
        });
      })
      .finally(() => setIsLoading(false));
  }, []);

  const getErrorDetails = (err) => {
    if (err.data) {
      if (typeof err.data === "string") return err.data;
      if (err.data["errors"]) {
        const keys = Object.keys(err.data["errors"]);
        return err.data["errors"][keys[0]];
      }
    }
    return err.toString();
  };

  const loadPagesParametersAsync = async () => {
    const search = location.search;
    const rawPath = new URLSearchParams(search).get("path");
    const path = normalizePathname(rawPath);
    if (!path) throw new Error("Path is required");

    switch (rawPath?.replace(/^\//, "")) {
      case "std/dvcs":
        await loadDevicesParametersAsync(search);
        break;
      case "std/sv":
        await loadSingleViewParametersAsync(search);
        break;
      case "std/gtw":
        await loadGatewayParametersAsync(search);
        break;
      default:
        throw new Error("Invalid path");
    }
  };

  const loadDevicesParametersAsync = async (search) => {
    const siteGuid = new URLSearchParams(search).get("siteGuid");
    if (!siteGuid) throw new Error("Site guid is required");

    const siteResponse = await safeApiGet(api.getSiteDetails, siteGuid);
    if (!siteResponse) return;
    const site = siteResponse.data;

    await ensureCorrectOrganization(site);

    setParameters({
      pathname: normalizePathname(new URLSearchParams(search).get("path")),
      site: {
        siteGuid,
        name: site.name,
        city: site.city,
        country: site.country,
        connectivitytype: "wifi",
        latitude: site.latitude,
        longitude: site.longitude,
      },
    });
  };

  const loadSingleViewParametersAsync = async (search) => {
    const deviceGuid = new URLSearchParams(search).get("deviceGuid");
    if (!deviceGuid) throw new Error("Device guid is required");

    const deviceResp = await safeApiGet(api.getDevice, deviceGuid);
    if (!deviceResp) return;
    const device = deviceResp.data;

    const siteResp = await safeApiGet(api.getSiteDetails, device.site.siteGuid);
    if (!siteResp) return;
    const site = siteResp.data;

    const gatewayResp = await safeApiGet(
      api.getSingleGatewayDetail,
      device.gateway.gatewayGuid,
    );
    if (!gatewayResp) return;
    const gatewayInfo = gatewayResp.data;

    await ensureCorrectOrganization(site);

    setParameters({
      pathname: normalizePathname(new URLSearchParams(search).get("path")),
      site: {
        siteGuid: device.site.siteGuid,
        gatewayGuid: device.gateway.gatewayGuid,
        deviceGuid,
        name: device.model,
        address: device.address,
        siteInfo: {
          siteGuid: device.site.siteGuid,
          name: device.site.name,
          city: site.city,
          country: site.country,
          connectivitytype: "wifi",
          latitude: site.latitude,
          longitude: site.longitude,
        },
      },
      gatewayInfo
    });
  };

  const loadGatewayParametersAsync = async (search) => {
    const macAddress = new URLSearchParams(search).get("macAddress");
    if (!macAddress) throw new Error("MAC address is required");

    const gatewayResp = await safeApiGet(api.getGatewayByMAC, macAddress);
    if (!gatewayResp) return;
    const gateway = gatewayResp.data.data[0];

    const siteResp = await safeApiGet(
      api.getSiteDetails,
      gateway.site.siteGuid,
    );
    if (!siteResp) return;
    const site = siteResp.data;

    await ensureCorrectOrganization(site);

    setParameters({
      pathname: normalizePathname(new URLSearchParams(search).get("path")),
      site: {
        siteGuid: gateway.site.siteGuid,
        gatewayGuid: gateway.gatewayGuid,
        name: gateway.name,
        address: gateway.address,
        siteInfo: {
          siteGuid: gateway.site.siteGuid,
          name: gateway.site.name,
          city: site.city,
          country: site.country,
          connectivitytype: "wifi",
          latitude: site.latitude,
          longitude: site.longitude,
        },
      },
      gateway,
    });
  };

  const safeApiGet = async (apiFunc, ...args) => {
    try {
      return await apiFunc(...args);
    } catch (err) {
      if (err?.status === 403 || err?.response?.status === 403) {
        setParameters({ pathname: "/std/index" });
        return null;
      }
      throw err;
    }
  };

  const ensureCorrectOrganization = async (site) => {
    const customerGuid = site.customer?.customerGuid;
    if (!customerGuid) throw new Error("Missing customer on site");

    // With the organization filter the page the link opens picks its own
    // organization (usePageOrganization): the filter may stay on the fleet's.
    if (isOrgFilterEnabled) return;

    if (activeOrganization?.guid !== customerGuid) {
      await setActiveOrganization(customerGuid);
    }
  };

  const normalizePathname = (path) => {
    if (!path) return path;
    return path.startsWith("/") ? path : `/${path}`;
  };

  if (isLoading) return <Loader />;

  if (parameters.pathname) {
    const { pathname, ...state } = parameters;
    return <Navigate to={pathname} state={state} replace />;
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <div>{parameters.error}</div>
      <div>{parameters.details}</div>
      <Button
        className="mt-2"
        color="primary"
        onClick={() => {
          setParameters({ pathname: "/std/index" });
        }}
      >
        Return to Home
      </Button>
    </div>
  );
};

export default ExternalLink;
